<script lang="ts">
import { mapActions, mapGetters } from 'vuex';
import {
  iAccordionItem,
  iAccordionItemSizes,
  iButton,
  iButtonTypes,
  iButtonStyles,
  iButtonSizes,
} from '@inscribe/signal';
import InfoIcon from '@/V2/components/InfoIcon';
import UserFeedback from '@/V2/components/user-feedback/UserFeedback.vue';
import palette from '@inscribe/signal/dist/palette';
import {
  getLastRun,
  mapRunToLabel,
  mapRunToStatus,
} from '@/V2/documents/document-view/document-review-agent/utils';
import { RUN_STATUS } from '@/constants/analyst-constants';
import { USER_FEEDBACK_RESOURCE_TYPE } from '@/constants/userFeedback';
import EmptyState from '@/V2/documents/document-view/document-review-agent/EmptyState.vue';
import StatusCard from '@/V2/components/status/StatusCard.vue';
import { Statuses } from '@/V2/components/status/utils';
import ProcessingEventsAccordion from '@/V2/components/processing-events/ProcessingEventsAccordion.vue';
import { registerChatAgentHookClickEvent } from '@/events-logger';
import { ChatContextType } from '@/types/chat';
import { SuggestedChatPrompt } from '@/constants/chat';
import { getSuggestedPrompts } from '@/utils/chat';
import { Document } from '@/types/document';

export default {
  components: {
    EmptyState,
    iButton,
    iAccordionItem,
    InfoIcon,
    StatusCard,
    UserFeedback,
    ProcessingEventsAccordion,
  },
  props: {
    document: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    runs: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      iAccordionItemSizes,
      palette,
      Statuses,
      USER_FEEDBACK_RESOURCE_TYPE,
      mapRunToLabel,
      mapRunToStatus,
      iButtonTypes,
      iButtonStyles,
      iButtonSizes,
    };
  },
  computed: {
    ...mapGetters([
      'isUserFeedbackEnabled',
      'isChatAgentEnabled',
      'currentConversationUuid',
      'getCachedProcessingEventsForDocumentId',
      'currentConversationUuid',
      'getIsGeneratingForConversationUuid',
    ]),
    documentId() {
      return this.document?.id;
    },
    lastRun() {
      return getLastRun(this.runs);
    },
    isProcessing() {
      return (
        !!this.lastRun && [RUN_STATUS.PENDING].includes(this.lastRun.status)
      );
    },
    agentIsRunning() {
      return (
        !!this.lastRun && [RUN_STATUS.RUNNING].includes(this.lastRun.status)
      );
    },
    agentIsFinished() {
      return !!this.lastRun && this.lastRun.status === RUN_STATUS.FINISHED;
    },
    documentIsFinished() {
      return Boolean(this.document?.risk_level || this.agentIsFinished);
    },
    errorState() {
      return !this.lastRun || this.lastRun.status === RUN_STATUS.FAILED;
    },
    emptyState() {
      return this.runs.length === 0;
    },
    processingEvents() {
      const cached = this.getCachedProcessingEventsForDocumentId(
        this.documentId,
      );
      return cached?.data || [];
    },
    shouldShowAnalysisSteps() {
      return this.processingEvents.length > 0;
    },
    suggestedPrompts() {
      return getSuggestedPrompts({
        chatContextType: ChatContextType.Document,
        document: this.document as Document,
      });
    },
    chatIsGenerating() {
      return this.getIsGeneratingForConversationUuid(
        this.currentConversationUuid,
      );
    },
  },
  methods: {
    ...mapActions(['submitConversationMessage', 'openChat']),
    openRiskAnalysisDetails() {
      this.$emit('openRiskAnalysisDetails');
    },
    onSuggestedPromptClicked(prompt: SuggestedChatPrompt) {
      this.openChat();
      this.submitConversationMessage({
        conversationUuid: this.currentConversationUuid,
        userMessageContent: prompt.expandedPrompt ?? prompt.prompt,
        contextCustomerUuids: [this.$route.params.customer_id],
        contextDocumentIds: [this.$route.params.document_id],
      });
      registerChatAgentHookClickEvent('document-page-suggested-prompt');
    },
  },
};
</script>

<template>
  <i-accordion-item
    class="document-review-risk-analysis"
    :mount-open="true"
    title="Summary"
    :no-background="true"
    :size="iAccordionItemSizes.LARGE"
  >
    <!-- title extra content -->
    <template #extra-title-content>
      <info-icon
        class="i-mr-4"
        title="Summary"
        description="Summarizes the overall risk level based on any identified fraud signals and the AI Fraud Analyst verifications."
        link-text="Learn more"
        link-external-url=""
      />
    </template>

    <div
      class="score-box d-flex flex-column i-gap-4 i-mb-8"
      @click="openRiskAnalysisDetails"
    >
      <status-card
        v-if="lastRun || isProcessing"
        :state="mapRunToStatus(lastRun)"
        :label="mapRunToLabel(lastRun)"
        :score="lastRun?.output?.trust_score"
      ></status-card>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading || isProcessing"
      class="loading-state accordion-content d-flex flex-column i-gap-8"
      data-testid="run-processing"
    >
      <div class="lines d-flex flex-column i-gap-4">
        <div
          v-for="(length, index) in [353, 389, 373]"
          :key="index"
          :style="{ width: length + 'px' }"
          class="line linear-background"
        ></div>
      </div>
    </div>

    <!-- empty space -->
    <empty-state
      v-else-if="emptyState"
      title="The Risk Analyst didn't run on this document yet"
      class="i-mb-10"
      data-testid="risk-analysis-empty"
    ></empty-state>

    <div
      v-else-if="errorState"
      class="i-mb-10 i-text-neutral-120 i-text-body-3"
      data-testid="risk-analysis-error"
    >
      There was an error while running the Risk Analyst
    </div>

    <!-- content -->
    <div v-else class="accordion-content d-flex flex-column i-gap-8">
      <div
        v-if="lastRun?.output?.analysis_paragraphs"
        class="paragraphs d-flex flex-column i-gap-4"
      >
        <div
          v-for="(paragraph, index) in lastRun?.output?.analysis_paragraphs"
          :key="index"
          class="paragraph i-text-neutral-120 i-text-body-3"
        >
          {{ paragraph }}
        </div>
      </div>

      <div
        class="d-flex flex-column align-items-start i-gap-4"
        v-if="isChatAgentEnabled"
      >
        <i-button
          v-for="prompt in suggestedPrompts"
          :key="prompt.prompt"
          class="d-flex flex-grow-1"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.SOLID_LIGHT"
          :size="iButtonSizes.SMALL"
          :label="prompt.prompt"
          :aria-label="`Document view: Suggested prompt '${prompt.prompt}'`"
          data-dd-action-name="document-view-suggested-prompt"
          @button-click="onSuggestedPromptClicked(prompt)"
          :disabled="chatIsGenerating"
        ></i-button>
      </div>

      <div
        v-if="isUserFeedbackEnabled && documentIsFinished"
        class="accuracy-feedback d-flex align-items-center justify-content-space-between i-radius-md i-pl-8 i-pr-4 i-py-4 i-text-neutral-120 i-text-body-3"
      >
        <div>Was this result accurate?</div>
        <user-feedback
          :resource-type="USER_FEEDBACK_RESOURCE_TYPE.DOCUMENT_REVIEW_AGENT_RUN"
          :resource-id="lastRun.id"
        />
      </div>

      <processing-events-accordion
        v-if="shouldShowAnalysisSteps"
        title="Analysis Steps"
        :key="agentIsRunning"
        :all-processing-events-finished="documentIsFinished"
        :start-expanded="agentIsRunning"
        :processing-events="processingEvents"
      />
    </div>
  </i-accordion-item>
</template>

<style lang="scss" scoped>
.document-review-risk-analysis {
  .paragraph {
    line-height: 18px !important;
  }

  .accuracy-feedback {
    background: $light-neutral-20;
    height: 36px;
  }

  .score-box {
    cursor: pointer;
  }

  .loading-state {
    .line {
      height: 10px;
      border-radius: 100px;
    }
  }
}
</style>
